<template>
  <div id="box">
    <div class="historyTag">
      <historyTag :historyArr="historyArr"></historyTag>
    </div>

    <el-alert
      type="info"
      :closable="false"
      show-icon
      style="margin-bottom: 12px"
      title="展示官方寄售商品同步至闲鱼的状态。上架默认延迟 30 分钟；失败可单条重试，也可使用「分批重试失败」。"
    />

    <div class="filter-box">
      <el-form :inline="true" size="small" @submit.native.prevent>
        <el-form-item label="商品编号">
          <el-input
            v-model.trim="filters.productNo"
            placeholder="如 G2026082100402723"
            clearable
            style="width: 200px"
            @keyup.enter.native="search"
          />
        </el-form-item>
        <el-form-item label="同步状态">
          <el-select v-model="filters.syncStatus" placeholder="全部" clearable style="width: 130px">
            <el-option v-for="s in statusOptions" :key="s.value" :label="s.label" :value="s.value" />
          </el-select>
        </el-form-item>
        <el-form-item label="闲鱼店铺">
          <el-select v-model="filters.accountId" placeholder="全部" clearable style="width: 160px">
            <el-option
              v-for="a in accountOptions"
              :key="a.id"
              :label="a.name"
              :value="a.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="search">搜索</el-button>
          <el-button @click="resetFilters">重置</el-button>
          <el-button class="btn-goofish" @click="openRetryFailedDialog">分批重试失败</el-button>
          <el-button type="text" @click="$router.push('/goofishAccount')">店铺账号管理</el-button>
        </el-form-item>
      </el-form>
    </div>

    <div class="table-scroll-wrap">
      <el-table :data="tableData" border style="width: 100%" v-loading="loading">
        <el-table-column label="商品" min-width="300" fixed="left" class-name="cell-title-wrap">
          <template slot-scope="scope">
            <div class="product-cell product-cell--with-image">
              <el-image
                v-if="coverUrl(scope.row)"
                class="product-cell__cover"
                :src="coverUrl(scope.row)"
                :preview-src-list="[coverUrl(scope.row)]"
                fit="cover"
              />
              <div v-else class="product-cell__cover product-cell__cover--empty">无图</div>
              <div class="product-cell__body">
                <router-link
                  v-if="scope.row.productNo"
                  class="product-cell__link"
                  :to="officialProductLink(scope.row)"
                >
                  {{ scope.row.productNo }}
                </router-link>
                <div v-else class="product-cell__no">—</div>
                <div v-if="productMeta(scope.row)" class="product-cell__meta">{{ productMeta(scope.row) }}</div>
                <div class="product-cell__title">
                  {{ scope.row.productTitle || "—" }}
                </div>
              </div>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="importCode" label="代号" width="88" align="center" show-overflow-tooltip>
          <template slot-scope="scope">{{ scope.row.importCode || "—" }}</template>
        </el-table-column>
        <el-table-column label="闲鱼店铺" width="120" align="center" show-overflow-tooltip>
          <template slot-scope="scope">
            <div>{{ scope.row.accountName || "—" }}</div>
            <div v-if="scope.row.accountUserName" class="sub-text">{{ scope.row.accountUserName }}</div>
          </template>
        </el-table-column>
        <el-table-column prop="sourceConsignShelfNo" label="货架号" width="88" align="center" show-overflow-tooltip>
          <template slot-scope="scope">{{ scope.row.sourceConsignShelfNo || "—" }}</template>
        </el-table-column>
        <el-table-column label="商品状态" width="88" align="center">
          <template slot-scope="scope">
            <el-tag :type="productStatusTagType(scope.row.productStatus)" size="mini">
              {{ productStatusLabel(scope.row) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="同步状态" width="108" align="center">
          <template slot-scope="scope">
            <el-tooltip
              v-if="scope.row.syncStatus === 'FAILED' && scope.row.lastError"
              effect="dark"
              placement="top"
              :content="scope.row.lastError"
            >
              <el-tag :type="statusTagType(scope.row.syncStatus)" size="mini">
                {{ statusLabel(scope.row.syncStatus) }}
              </el-tag>
            </el-tooltip>
            <el-tag v-else :type="statusTagType(scope.row.syncStatus)" size="mini">
              {{ statusLabel(scope.row.syncStatus) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="同步价" width="118" align="center">
          <template slot-scope="scope">
            <div>{{ formatMoney(scope.row.priceSnapshot) }}</div>
            <div v-if="scope.row.freightSnapshot != null" class="sub-text">
              含运费 {{ formatMoney(scope.row.freightSnapshot) }}
            </div>
          </template>
        </el-table-column>
        <el-table-column label="当前价" width="92" align="center">
          <template slot-scope="scope">
            <span :class="{ 'price-diff': isPriceDiff(scope.row) }">
              {{ formatMoney(scope.row.currentPrice) }}
            </span>
          </template>
        </el-table-column>
        <el-table-column label="闲鱼商品ID" width="148" align="center" show-overflow-tooltip>
          <template slot-scope="scope">{{ scope.row.xianyuItemId || "—" }}</template>
        </el-table-column>
        <el-table-column label="同步时间" width="168" align="center">
          <template slot-scope="scope">
            <template v-if="showScheduledTime(scope.row)">
              <div>计划 {{ formatTime(scope.row.scheduledAt) }}</div>
            </template>
            <template v-else>
              <div>{{ formatTime(scope.row.lastSyncAt) }}</div>
            </template>
          </template>
        </el-table-column>
        <el-table-column label="最近错误" min-width="160" show-overflow-tooltip>
          <template slot-scope="scope">
            <span v-if="scope.row.lastError" class="error-text">{{ scope.row.lastError }}</span>
            <span v-else>—</span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="380" align="center" fixed="right">
          <template slot-scope="scope">
            <el-button
              v-if="scope.row.productNo"
              size="mini"
              type="text"
              @click="goOfficialProduct(scope.row)"
            >
              查看商品
            </el-button>
            <el-button
              v-if="canMarkAuctionSold(scope.row)"
              size="mini"
              class="btn-goofish"
              :loading="actionId === scope.row.productId && actionType === 'auctionSold'"
              @click="openAuctionSoldDialog(scope.row)"
            >
              拍卖售出
            </el-button>
            <el-button
              size="mini"
              class="btn-goofish-plain"
              :loading="actionId === scope.row.productId && actionType === 'schedule'"
              @click="doAction('schedule', scope.row)"
            >
              延迟同步
            </el-button>
            <el-button
              size="mini"
              class="btn-goofish"
              :loading="actionId === scope.row.productId && actionType === 'now'"
              @click="doAction('now', scope.row)"
            >
              立即同步
            </el-button>
            <el-button
              v-if="scope.row.syncStatus === 'FAILED'"
              size="mini"
              class="btn-goofish"
              :loading="actionId === scope.row.productId && actionType === 'retry'"
              @click="doAction('retry', scope.row)"
            >
              重试
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <div class="page_box">
      <el-pagination
        @current-change="changePage"
        :current-page.sync="currentPage"
        :page-size="pageSize"
        layout="total, prev, pager, next, jumper"
        :total="tableTotal"
      />
    </div>

    <el-dialog title="分批重试同步失败" :visible.sync="retryFailedVisible" width="520px">
      <p class="retry-failed-tip">
        从状态为<strong>失败</strong>的记录中<strong>随机</strong>取出一批商品，提交后台异步重试。
        建议每批 100～200 条；600 多条可多点几次，直至失败列表清空。
        非在售商品会失败跳过。
      </p>
      <el-form label-width="96px" size="small">
        <el-form-item label="闲鱼店铺">
          <el-select v-model="retryFailedAccountId" clearable placeholder="全部失败店铺" style="width: 100%">
            <el-option
              v-for="a in accountOptions"
              :key="a.id"
              :label="a.name"
              :value="a.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="同步方式">
          <el-radio-group v-model="retryFailedMode">
            <el-radio label="schedule">延迟 30 分钟后同步（推荐）</el-radio>
            <el-radio label="now">立即同步上架</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="本批条数">
          <el-input-number v-model="retryFailedLimit" :min="1" :max="1000" :step="50" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="retryFailedVisible = false">取消</el-button>
        <el-button class="btn-goofish" :loading="retryFailedSubmitting" @click="submitRetryFailed">
          确认提交本批
        </el-button>
      </span>
    </el-dialog>

    <el-dialog title="登记闲鱼拍卖售出" :visible.sync="auctionSoldVisible" width="480px" @closed="resetAuctionSoldForm">
      <el-form :model="auctionSoldForm" label-width="108px" size="small">
        <el-form-item label="商品">
          <div>{{ auctionSoldRow && auctionSoldRow.productTitle ? auctionSoldRow.productTitle : "—" }}</div>
        </el-form-item>
        <el-form-item label="闲鱼订单号">
          <el-input v-model.trim="auctionSoldForm.goofishOrderNo" placeholder="选填，无则系统自动生成" />
        </el-form-item>
        <el-form-item label="成交金额">
          <el-input v-model.trim="auctionSoldForm.orderAmount" placeholder="请填闲鱼落槌价（勿填挂牌价）" />
        </el-form-item>
        <el-form-item label="备注">
          <el-input v-model.trim="auctionSoldForm.note" type="textarea" :rows="2" placeholder="选填" />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="auctionSoldVisible = false">取消</el-button>
        <el-button type="primary" class="btn-goofish" :loading="auctionSoldSubmitting" @click="submitAuctionSold">
          确认登记
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { returnTime } from "@/util/utils";

export default {
  name: "GoofishSyncList",
  data() {
    return {
      historyArr: ["商品管理", "闲鱼商品同步列表"],
      loading: false,
      tableData: [],
      tableTotal: 0,
      currentPage: 1,
      pageSize: 20,
      actionId: null,
      actionType: "",
      retryFailedVisible: false,
      retryFailedSubmitting: false,
      retryFailedMode: "schedule",
      retryFailedLimit: 200,
      auctionSoldVisible: false,
      auctionSoldSubmitting: false,
      auctionSoldRow: null,
      auctionSoldForm: {
        goofishOrderNo: "",
        orderAmount: "",
        note: "",
      },
      filters: {
        productNo: "",
        syncStatus: "",
        accountId: "",
      },
      accountOptions: [],
      retryFailedAccountId: null,
      statusOptions: [
        { value: "PENDING", label: "待处理" },
        { value: "SCHEDULED", label: "已排期" },
        { value: "PUBLISHING", label: "上架中" },
        { value: "PUBLISHED", label: "已上架" },
        { value: "OFFLINE", label: "已下架" },
        { value: "SOLD", label: "已售出" },
        { value: "FAILED", label: "失败" },
        { value: "CANCELLED", label: "已取消" },
      ],
    };
  },
  mounted() {
    const q = this.$route && this.$route.query;
    if (q && q.productNo) {
      this.filters.productNo = String(q.productNo);
    }
    this.loadAccounts();
    this.fetchList(1);
  },
  methods: {
    loadAccounts() {
      this.$https(this.$api.api.goofishAccountListEnabled, {}, "GET").then((res) => {
        if (res.data && res.data.code == 200) {
          this.accountOptions = res.data.data || [];
        }
      });
    },
    formatMoney(v) {
      if (v == null || v === "") return "—";
      const n = Number(v);
      return Number.isFinite(n) ? n.toFixed(2) : String(v);
    },
    formatTime(t) {
      if (!t) return "—";
      try {
        return returnTime(t, "-", "s");
      } catch (e) {
        return String(t);
      }
    },
    statusLabel(v) {
      const hit = this.statusOptions.find((s) => s.value === v);
      return hit ? hit.label : v || "—";
    },
    statusTagType(v) {
      const map = {
        PUBLISHED: "success",
        SCHEDULED: "info",
        PUBLISHING: "warning",
        FAILED: "danger",
        SOLD: "",
        OFFLINE: "info",
        CANCELLED: "info",
      };
      return map[v] || "";
    },
    coverUrl(row) {
      if (!row || !row.productCoverImage) return "";
      return this.$assetUrl ? this.$assetUrl(row.productCoverImage) : row.productCoverImage;
    },
    productMeta(row) {
      if (!row) return "";
      const brand = String(row.brandName || "").trim();
      const type = String(row.typeName || "").trim();
      const grade = String(row.gradeLevel || "").trim();
      const parts = [];
      const name = `${brand} ${type}`.trim();
      if (name) parts.push(name);
      if (grade) parts.push(`${grade}级`);
      return parts.join(" · ");
    },
    productStatusLabel(row) {
      if (!row) return "—";
      const status = row.productStatus;
      if (status === 1) return "在售";
      if (status === 0) return "已下架";
      if (status === 2) return "已售出";
      if (status === 8) return "拍卖中";
      return row.productStatusName || "—";
    },
    productStatusTagType(status) {
      const map = {
        1: "success",
        2: "info",
        8: "warning",
        0: "info",
      };
      return map[status] || "";
    },
    isPriceDiff(row) {
      if (!row || row.currentPrice == null || row.priceSnapshot == null) return false;
      return Number(row.currentPrice) !== Number(row.priceSnapshot);
    },
    showScheduledTime(row) {
      const status = row && row.syncStatus;
      return (status === "SCHEDULED" || status === "PENDING") && row.scheduledAt;
    },
    officialProductLink(row) {
      return {
        path: "/secondhandOfficialProduct",
        query: row && row.productNo ? { productNo: row.productNo } : {},
      };
    },
    goOfficialProduct(row) {
      if (!row) return;
      this.$router.push(this.officialProductLink(row));
    },
    buildQuery(page) {
      const payload = { page: page || 1, size: this.pageSize };
      const productNo = String(this.filters.productNo || "").trim();
      if (productNo) payload.productNo = productNo;
      if (this.filters.syncStatus) payload.syncStatus = this.filters.syncStatus;
      if (this.filters.accountId) payload.accountId = this.filters.accountId;
      return payload;
    },
    fetchList(page) {
      this.loading = true;
      this.$https(this.$api.api.goofishSyncList, this.buildQuery(page), "GET")
        .then((res) => {
          if (res.data && res.data.code == 200 && res.data.data) {
            this.tableData = res.data.data.records || [];
            this.tableTotal = res.data.data.total || 0;
          } else {
            this.tableData = [];
            this.tableTotal = 0;
            this.$message.error((res.data && res.data.msg) || "查询失败");
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    search() {
      this.currentPage = 1;
      this.fetchList(1);
    },
    resetFilters() {
      this.filters = { productNo: "", syncStatus: "", accountId: "" };
      this.currentPage = 1;
      this.fetchList(1);
    },
    changePage(p) {
      this.currentPage = p;
      this.fetchList(p);
    },
    openRetryFailedDialog() {
      this.retryFailedMode = "schedule";
      this.retryFailedAccountId = this.filters.accountId || null;
      if (!this.retryFailedLimit) this.retryFailedLimit = 200;
      this.retryFailedVisible = true;
    },
    submitRetryFailed() {
      const mode = this.retryFailedMode || "schedule";
      const limit = Math.min(Math.max(Number(this.retryFailedLimit) || 200, 1), 1000);
      const payload = { mode, limit };
      if (this.retryFailedAccountId) payload.accountId = this.retryFailedAccountId;
      this.retryFailedSubmitting = true;
      this.$https(this.$api.api.goofishSyncRetryFailed, payload, "POST")
        .then((res) => {
          if (res.data && res.data.code == 200) {
            const data = res.data.data || {};
            this.retryFailedVisible = false;
            this.$message.success(
              res.data.msg ||
                (data.asyncAccepted
                  ? `已提交后台重试本批 ${Number(data.total) || 0} 条`
                  : "当前没有同步失败记录")
            );
            this.filters.syncStatus = "FAILED";
            this.currentPage = 1;
            this.fetchList(1);
          } else {
            this.$message.error((res.data && res.data.msg) || "提交失败");
          }
        })
        .finally(() => {
          this.retryFailedSubmitting = false;
        });
    },
    doAction(type, row) {
      const productId = row && row.productId;
      if (!productId) return;
      const apiMap = {
        schedule: this.$api.api.goofishSyncSchedulePublish,
        now: this.$api.api.goofishSyncPublishNow,
        retry: this.$api.api.goofishSyncRetry,
      };
      const msgMap = {
        schedule: "确认排期 30 分钟后同步至闲鱼？",
        now: "确认立即同步上架至闲鱼？",
        retry: "确认重试闲鱼同步？",
      };
      this.$confirm(msgMap[type], "提示", { type: "warning" })
        .then(() => {
          this.actionId = productId;
          this.actionType = type;
          const payload = { productId };
          if (row.accountId) {
            if (type === "retry") payload.accountId = row.accountId;
            else payload.accountIds = [row.accountId];
          }
          this.$https(apiMap[type], payload, "POST")
            .then((res) => {
              if (res.data && res.data.code == 200) {
                this.$message.success((res.data && res.data.msg) || "操作成功");
                this.fetchList(this.currentPage);
              } else {
                this.$message.error((res.data && res.data.msg) || "操作失败");
              }
            })
            .finally(() => {
              this.actionId = null;
              this.actionType = "";
            });
        })
        .catch(() => {});
    },
    canMarkAuctionSold(row) {
      if (!row || !row.productId) return false;
      const status = Number(row.productStatus);
      return status === 8 || status === 1 || status === 0;
    },
    openAuctionSoldDialog(row) {
      this.auctionSoldRow = row;
      this.auctionSoldForm = {
        goofishOrderNo: "",
        orderAmount: row.currentPrice != null ? String(row.currentPrice) : "",
        note: "",
      };
      this.auctionSoldVisible = true;
    },
    resetAuctionSoldForm() {
      this.auctionSoldRow = null;
      this.auctionSoldForm = { goofishOrderNo: "", orderAmount: "", note: "" };
      this.auctionSoldSubmitting = false;
    },
    submitAuctionSold() {
      const row = this.auctionSoldRow;
      if (!row || !row.productId) return;
      const payload = {
        productId: row.productId,
        goofishOrderNo: String(this.auctionSoldForm.goofishOrderNo || "").trim() || undefined,
        note: String(this.auctionSoldForm.note || "").trim() || undefined,
      };
      const amount = String(this.auctionSoldForm.orderAmount || "").trim();
      if (amount) {
        const n = Number(amount);
        if (!Number.isFinite(n) || n <= 0) {
          this.$message.error("成交金额无效");
          return;
        }
        payload.orderAmount = n;
      }
      this.auctionSoldSubmitting = true;
      this.actionId = row.productId;
      this.actionType = "auctionSold";
      this.$https(this.$api.api.goofishMarkAuctionSold, payload, "POST")
        .then((res) => {
          if (res.data && res.data.code == 200) {
            this.$message.success((res.data && res.data.msg) || "已登记");
            this.auctionSoldVisible = false;
            this.fetchList(this.currentPage);
          } else {
            this.$message.error((res.data && res.data.msg) || "登记失败");
          }
        })
        .finally(() => {
          this.auctionSoldSubmitting = false;
          this.actionId = null;
          this.actionType = "";
        });
    },
  },
};
</script>

<style scoped lang="less">
.filter-box {
  margin: 12px 0;
  padding: 12px 12px 0;
  background: #fafafa;
  border: 1px solid #ebeef5;
  border-radius: 4px;
}
.retry-failed-tip {
  margin: 0 0 16px;
  color: #606266;
  line-height: 1.6;
  font-size: 13px;
}
.page_box {
  margin-top: 16px;
  text-align: right;
}
.product-cell {
  line-height: 1.45;
  text-align: left;
}
.product-cell--with-image {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}
.product-cell__cover {
  width: 56px;
  height: 56px;
  border-radius: 4px;
  flex-shrink: 0;
  overflow: hidden;
  background: #f5f7fa;
}
.product-cell__cover--empty {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #c0c4cc;
  font-size: 12px;
}
.product-cell__body {
  min-width: 0;
  flex: 1;
}
.product-cell__no {
  color: #303133;
  font-weight: 600;
}
.product-cell__link {
  color: #409eff;
  font-weight: 600;
  text-decoration: none;
}
.product-cell__link:hover {
  text-decoration: underline;
}
.product-cell__meta {
  margin-top: 2px;
  color: #909399;
  font-size: 12px;
}
.product-cell__title {
  margin-top: 2px;
  color: #606266;
  white-space: normal;
  word-break: break-word;
  overflow-wrap: anywhere;
  line-height: 1.4;
}
.sub-text {
  color: #909399;
  font-size: 12px;
  line-height: 1.3;
}
.price-diff {
  color: #e6a23c;
  font-weight: 600;
}
.error-text {
  color: #f56c6c;
}
</style>



// WEBPACK FOOTER //
// src/pages/goofish/sync/index.vue